/* eslint-disable max-lines */
import dayjs from "dayjs";
import { FC, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import useFetch from "../../hooks/useFetch";
import {
  CREDIT_TYPE_LABELS,
  CreditRedeemableScope,
  ENTITY_NAME_LABELS,
  IWalletLedgerEntry,
  labelFor,
  REDEEMABLE_SCOPE_LABELS,
  SCOPE_NAME_LABELS
} from "../../interfaces/wallet/Wallet.types";
import {
  getWalletDetail,
  getWalletLedger
} from "../../services/Wallet.service";
import { eur } from "../../utils/eur";
import Table, { Row } from "../ui/table";
import Pagination from "../ui/table/Pagination";
import Can from "../users/permissions/Can";
import BalanceFigures from "./BalanceFigures";
import IssueCreditsModal from "./IssueCreditsModal";
import RevokeCreditsModal from "./RevokeCreditsModal";

const ledgerColumns: (string | number)[] = [
  "Data",
  "Veprimi",
  "Shuma",
  "Ku vlen",
  "Lidhur me",
  "Përdor deri",
  "Arsyeja",
  "Kryer nga"
];

const LEDGER_PAGE_SIZE = 50;

// A per-lot expiry row points at another ledger row, so its id means nothing to an agent.
const entityLabel = (entry: IWalletLedgerEntry) => {
  if (!entry.entityName) return "-";

  const label = labelFor(ENTITY_NAME_LABELS, entry.entityName);
  return entry.entityName === "WalletCredit" ? label : `${label} #${entry.entityId}`;
};

const issuedByLabel = (entry: IWalletLedgerEntry) => {
  if (entry.issuedByAdminName) return entry.issuedByAdminName;
  return entry.issuedByAdminId ? `Admin #${entry.issuedByAdminId}` : "sistemi";
};

const WalletDetail: FC = () => {
  const { walletId } = useParams<{ walletId: string }>();
  const id = Number(walletId);

  const {
    response: detail,
    loading: detailLoading,
    error: detailError,
    handleFunc: loadDetail
  } = useFetch(getWalletDetail);

  const {
    response: ledger,
    loading: ledgerLoading,
    error: ledgerError,
    handleFunc: loadLedger
  } = useFetch(getWalletLedger);

  const [page, setPage] = useState(1);

  const reload = () => {
    void loadDetail({ walletId: id });
    void loadLedger({ walletId: id, pageNumber: page, pageSize: LEDGER_PAGE_SIZE });
  };

  useEffect(() => {
    if (!Number.isNaN(id)) reload();
  }, [id, page]);

  const rows: IWalletLedgerEntry[] = ledger?.data?.result ?? [];
  const summary = detail?.data?.summary;
  const breakdown = detail?.data?.breakdown;
  const openLots = detail?.data?.openLots ?? [];

  // Collapse the lots into one line per (scope, expiry day). Kept in insertion order of first
  // appearance, which is already expiry-ascending from the API, so the soonest lapse stays on top.
  const lotGroups = Object.values(
    openLots.reduce<
      Record<
        string,
        {
          key: string;
          scope: CreditRedeemableScope;
          expiresAtUtc: string | null;
          remaining: number;
          count: number;
        }
      >
    >((acc, lot) => {
      const day = lot.expiresAtUtc
        ? dayjs(lot.expiresAtUtc).format("YYYY-MM-DD")
        : "never";
      const scope = lot.scope;
      const key = `${scope}|${day}`;
      acc[key] ??= {
        key,
        scope,
        expiresAtUtc: lot.expiresAtUtc,
        remaining: 0,
        count: 0
      };
      acc[key].remaining += lot.remaining;
      acc[key].count += 1;
      return acc;
    }, {})
  );

  const overdrawn = detail?.data?.overdrawn ?? 0;

  return (
    <div className="px-5 pb-8">
      {overdrawn > 0 && (
        <p
          className={
            "mb-4 rounded-md border border-red-300 bg-red-100/50 px-3 py-2 text-sm " +
            "text-red-700 tabular-nums"
          }
        >
          Libri i kësaj kuletës është në minus me {eur(overdrawn)}: janë shpenzuar
          më shumë kredite se sa janë dhënë (shpenzime paralele para se të
          mbrohej libri). Bilanci shfaqet i kufizuar në zero.
        </p>
      )}
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h2 className="text-lg font-semibold">{summary?.email ?? "..."}</h2>
        </div>
        <div className="flex items-center gap-4">
          <Can permissions="wallet.credit.revoke">
            {summary?.email && (
              <RevokeCreditsModal email={summary.email} onSuccess={reload} />
            )}
          </Can>
          <Can permissions="wallet.credit.issue">
            <IssueCreditsModal
              defaultEmail={summary?.email}
              onSuccess={reload}
            />
          </Can>
        </div>
      </div>

      {(!!detailError || !!ledgerError) &&
        !detailLoading &&
        !ledgerLoading && (
        <p className="text-sm text-red mb-4">
          Të dhënat nuk u ngarkuan dot.{" "}
          <button type="button" className="underline" onClick={reload}>
            Provo përsëri
          </button>
        </p>
      )}

      {breakdown && !detailLoading && (
        <div className="mb-6">
          <BalanceFigures breakdown={breakdown} />
        </div>
      )}

      {lotGroups.length > 0 && (
        <div className="mb-6">
          <h3 className="text-sm font-medium mb-2">Kredite të papërdorura</h3>
          {/* By scope and expiry date: eSIM cashback alone produces hundreds of one-cent lots. */}
          <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
            <div className="flex flex-wrap items-baseline gap-x-3 px-4 py-2 text-xs text-gray-500">
              <span className="w-20">Shuma</span>
              <span className="w-32">Ku vlen</span>
              <span>Përdor deri</span>
            </div>
            {lotGroups.map((g) => (
              <div
                key={g.key}
                className="flex flex-wrap items-baseline gap-x-3 px-4 py-2 text-sm"
              >
                <span className="font-medium w-20">{eur(g.remaining)}</span>
                <span className="text-gray-600 w-32">
                  {REDEEMABLE_SCOPE_LABELS[g.scope]}
                </span>
                <span className="text-gray-600">
                  {g.expiresAtUtc
                    ? dayjs(g.expiresAtUtc).format("DD/MM/YYYY")
                    : "pa afat"}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <h3 className="text-sm font-medium mb-2">Historiku</h3>
      <Table titles={ledgerColumns} loading={ledgerLoading}>
        {rows.map((e) => (
          <Row
            key={e.id}
            date={dayjs(e.date).format("DD/MM/YYYY HH:mm")}
            type={labelFor(CREDIT_TYPE_LABELS, e.type)}
            amount={
              <span
                className={`font-medium ${
                  e.amount < 0 ? "text-red" : "text-green"
                }`}
              >
                {e.amount < 0 ? "" : "+"}
                {eur(e.amount)}
              </span>
            }
            scope={labelFor(SCOPE_NAME_LABELS, e.redeemableScope)}
            entity={entityLabel(e)}
            expiry={
              e.expiresAtUtc
                ? dayjs(e.expiresAtUtc).format("DD/MM/YY")
                : "pa afat"
            }
            note={
              <span className="block max-w-xs truncate" title={e.note ?? ""}>
                {e.note ?? "-"}
              </span>
            }
            issuedBy={issuedByLabel(e)}
          />
        ))}
      </Table>

      <Pagination
        onPageChange={({ selected }) => setPage(selected + 1)}
        totalCount={ledger?.data?.totalCount ?? 0}
        page={page}
        length={rows.length}
        pageSize={ledger?.data?.pageSize ?? LEDGER_PAGE_SIZE}
        viewingOf={false}
        className="border-none px-0 py-0"
      />
    </div>
  );
};

export default WalletDetail;
